import React, { useContext, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { BranchSwitcherDialog, useSwitchableBranches } from "./BranchSwitcher";

/* ============================================================================
 * Creating something while looking at every branch
 * ----------------------------------------------------------------------------
 * A new row has to record ONE branch. While somebody is viewing all branches
 * there is no honest way to pick one, so creation stops and asks.
 *
 * WHY NOT JUST DEFAULT TO THE MAIN BRANCH
 * Because that is silent and permanent. A head reviewing the whole store and
 * raising three tickets as they go would have all three filed against the main
 * branch, and the branch-wise report they were reading at the time would then
 * be wrong in a way nobody can see afterwards — the rows look complete, they
 * are just attributed to the wrong shop. A moment's friction now beats a
 * corrupted report later.
 *
 * WHY THE BUTTON IS NOT LITERALLY DISABLED
 * A disabled button fires no click, so it can explain nothing — it just sits
 * there greyed out and the person has to guess. Instead the create handler is
 * wrapped: the click is intercepted, nothing is saved, and the branch picker
 * opens saying why. Use `blocked` to grey the button's APPEARANCE if you want
 * the visual cue as well; keep it clickable.
 *
 *   const { blocked, guard, guardDialog } = useBranchCreateGuard();
 *
 *   <Button onClick={guard(handleSave)} sx={{ opacity: blocked ? 0.6 : 1 }}>
 *     Save
 *   </Button>
 *   {guardDialog}
 *
 * `guard` is a pass-through for everyone else — a person in one branch, and a
 * service role, who has no branch of their own and never has had. Wrapping a
 * handler costs those callers nothing, so screens do not need a condition
 * around it.
 * ========================================================================== */

const DEFAULT_REASON =
  "You are viewing every branch. Pick the branch this belongs to — it has to " +
  "be recorded against one.";

export const useBranchCreateGuard = ({ reason = DEFAULT_REASON } = {}) => {
  const { branchScope } = useContext(AutoTaskAuthContext) || {};
  const [open, setOpen] = useState(false);

  // Absent context (a screen rendered before hydration) counts as allowed.
  // Blocking on a value that has not arrived yet would stop creation on every
  // screen for the first moment of its life.
  const blocked = branchScope ? !branchScope.canCreate : false;

  // The branch list is only fetched once creation is actually blocked. This
  // hook sits on every create screen in the app and the dialog it feeds opens
  // almost never, so an eager fetch would be a request per screen for a list
  // nobody is going to see.
  const { branches, loading } = useSwitchableBranches({ enabled: blocked });

  /**
   * Wrap a create/save handler. Runs it when a branch is settled; otherwise
   * swallows the click and opens the picker.
   */
  const guard =
    (fn) =>
    (...args) => {
      if (!blocked) return fn?.(...args);
      setOpen(true);
      return undefined;
    };

  // Rendered by the host so the dialog sits inside its tree (and so a screen
  // with several create buttons still has exactly one).
  const guardDialog = (
    <BranchSwitcherDialog
      open={open}
      onClose={() => setOpen(false)}
      branches={branches}
      loading={loading}
      // No "All branches" row: the whole point of this dialog is to leave that
      // view, so offering it back would be a way of answering "which branch?"
      // with "all of them".
      canViewAll={false}
      viewingAll
      title="Which branch is this for?"
      reason={reason}
    />
  );

  return { blocked, guard, guardDialog, openBranchPicker: () => setOpen(true) };
};

/**
 * The standing notice for a screen that is mostly a create form, where a person
 * would otherwise fill the whole thing in before finding out it cannot be
 * saved. Renders nothing when creation is fine.
 */
export const BranchCreateBanner = ({ what = "anything", sx = {} }) => {
  const { branchScope } = useContext(AutoTaskAuthContext) || {};
  const blocked = Boolean(branchScope) && !branchScope.canCreate;
  const { branches, loading } = useSwitchableBranches({ enabled: blocked });
  const [open, setOpen] = useState(false);

  // After the hooks, never before: an early return above them would change how
  // many hooks this component calls between renders.
  if (!blocked) return null;

  return (
    <Box sx={{ mb: 1.5, ...sx }}>
      <Alert
        severity="info"
        action={
          <Button
            size="small"
            onClick={() => setOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Pick a branch
          </Button>
        }
        sx={{ fontSize: "0.8rem", alignItems: "center" }}
      >
        You are viewing every branch, so {what} cannot be saved yet — it has to
        be recorded against one branch.
      </Alert>

      <BranchSwitcherDialog
        open={open}
        onClose={() => setOpen(false)}
        branches={branches}
        loading={loading}
        canViewAll={false}
        viewingAll
        title="Which branch is this for?"
        reason={DEFAULT_REASON}
      />
    </Box>
  );
};

export default useBranchCreateGuard;
